import { createSlice } from "@reduxjs/toolkit";
import { factory } from "../../api/apiFactory";
import extandDayjs from "../../helpers/dayjs";
import imageCompression from "browser-image-compression";
import { showNotification } from "./notificationSlice";
const kpiApi = factory.get("kpi");

const initialState = {
  // ── Leaves ──
  leaves: {
    loading: false,
    type_summary: {},
    summary: {},
    six_months_summary: {},
    stats: {
      totalRequests: 47,
      approved: 38,
      pending: 6,
      rejected: 3,
    },
    trendData: [
      { month: "ديسمبر", approved: 32, pending: 5, rejected: 2 },
      { month: "يناير", approved: 38, pending: 4, rejected: 3 },
      { month: "فبراير", approved: 29, pending: 6, rejected: 1 },
      { month: "مارس", approved: 41, pending: 3, rejected: 4 },
      { month: "أبريل", approved: 36, pending: 7, rejected: 2 },
      { month: "مايو", approved: 38, pending: 6, rejected: 3 },
    ],
    typeBreakdown: [
      {
        name: "سنوية",
        requests: 22,
        totalDays: 120,
        remaining: 480,
        status: "normal",
      },
      {
        name: "مرضية",
        requests: 12,
        totalDays: 45,
        remaining: 155,
        status: "watch",
      },
      {
        name: "طارئة",
        requests: 8,
        totalDays: 18,
        remaining: 62,
        status: "normal",
      },
      {
        name: "أمومة / أبوة",
        requests: 3,
        totalDays: 90,
        remaining: 30,
        status: "normal",
      },
      {
        name: "بدون راتب",
        requests: 2,
        totalDays: 12,
        remaining: null,
        status: "high",
      },
    ],
    typeChartData: [
      { name: "سنوية", value: 120 },
      { name: "مرضية", value: 45 },
      { name: "طارئة", value: 18 },
      { name: "أمومة", value: 90 },
      { name: "بدون راتب", value: 12 },
    ],
  },

  // ── Payroll ──
  payroll: {
    loading: false,
    kpi: {},
    ranges: {},
    summary: {},
    yearly_stats: {},
    stats: {
      totalSalaries: 485000,
      avgSalary: 1393,
      totalBonuses: 32000,
      totalDeductions: 8200,
      overtimeBonuses: 15400,
      activeLoans: 24,
    },
    monthlyTrend: [
      { month: "يناير", base: 460, bonuses: 25, overtime: 12 },
      { month: "فبراير", base: 462, bonuses: 28, overtime: 14 },
      { month: "مارس", base: 465, bonuses: 22, overtime: 11 },
      { month: "أبريل", base: 470, bonuses: 30, overtime: 16 },
      { month: "مايو", base: 468, bonuses: 32, overtime: 15 },
      { month: "يونيو", base: 475, bonuses: 27, overtime: 14 },
      { month: "يوليو", base: 472, bonuses: 35, overtime: 17 },
      { month: "أغسطس", base: 480, bonuses: 30, overtime: 15 },
      { month: "سبتمبر", base: 478, bonuses: 28, overtime: 13 },
      { month: "أكتوبر", base: 482, bonuses: 33, overtime: 16 },
      { month: "نوفمبر", base: 483, bonuses: 31, overtime: 15 },
      { month: "ديسمبر", base: 485, bonuses: 32, overtime: 15 },
    ],
    distributionData: [
      { name: "< 500", value: 28 },
      { name: "500-1000", value: 85 },
      { name: "1000-2000", value: 142 },
      { name: "2000-3000", value: 68 },
      { name: "> 3000", value: 25 },
    ],
    summaryRows: [
      {
        item: "الراتب الأساسي",
        employees: 348,
        amount: 485000,
        pct: "88.5%",
        change: "+0.4%",
        trend: "up",
      },
      {
        item: "مكافآت الأداء",
        employees: 87,
        amount: 32000,
        pct: "5.8%",
        change: "+3.2%",
        trend: "up",
      },
      {
        item: "مكافآت الأوقات الإضافية",
        employees: 124,
        amount: 15400,
        pct: "2.8%",
        change: "ثابت",
        trend: "neutral",
      },
      {
        item: "خصومات الغياب",
        employees: 45,
        amount: -6200,
        pct: "1.1%",
        change: "+8%",
        trend: "down",
      },
      {
        item: "خصومات التأخير",
        employees: 62,
        amount: -2000,
        pct: "0.4%",
        change: "-2%",
        trend: "up",
      },
      {
        item: "الصافي",
        employees: 348,
        amount: 524200,
        pct: "100%",
        change: "+1.2%",
        trend: "up",
        isTotal: true,
      },
    ],
  },

  // ── Overview ──
  overview: {
    loading: false,
    summary: {},
    attendance_summary: [],
    types_employees_summary: {},
    departments: [],
    genders: [],
  },

  // Active tab
  activeTab: "overview",
};

const kpiSlice = createSlice({
  name: "kpi",
  initialState,
  reducers: {
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
    setLoading: (state, action) => {
      state[action.payload].loading = !state[action.payload].loading;
    },
    setSalaryKpi: (state, { payload }) => {
      state.payroll.kpi = payload.data.kpi;
      state.payroll.ranges = payload.data.ranges;
      state.payroll.summary = payload.data.summary;
      state.payroll.yearly_stats = payload.data.yearly_stats;
    },
    setLeavesKpi: (state, { payload }) => {
      state.leaves.type_summary = payload.data.type_summary;
      state.leaves.summary = payload.data.summary;
      state.leaves.six_months_summary = payload.data.six_months_summary;
    },
    setOverviewKpi: (state, { payload }) => {
      state.overview.summary = payload.data.summary;
      state.overview.attendance_summary = payload.data.attendance_summary;
      state.overview.types_employees_summary = payload.data.types_employees_summary;
      state.overview.departments = payload.data.departments;
      state.overview.genders = payload.data.genders;
    },
  },
});

export const { setActiveTab, setLoading, setSalaryKpi, setLeavesKpi, setOverviewKpi } = kpiSlice.actions;
export default kpiSlice.reducer;

const getPayrollKpi = () => async (dispatch, getState) => {
  try {
    dispatch(setLoading("payroll"));
    const res = await kpiApi.payrollKpi();
    dispatch(setSalaryKpi(res.data));
    dispatch(setLoading("payroll"));
  } catch (err) {
    dispatch(setLoading("payroll"));
    throw new Error(err);
  }
};
const getLeavesKpi = () => async (dispatch, getState) => {
  try {
    dispatch(setLoading("leaves"));
    const res = await kpiApi.leaveKpi();
    dispatch(setLeavesKpi(res.data));
    dispatch(setLoading("leaves"));
  } catch (err) {
    dispatch(setLoading("leaves"));
    throw new Error(err);
  }
};
const getOverviewKpi = () => async (dispatch, getState) => {
  try {
    dispatch(setLoading("overview"));
    const res = await kpiApi.globalKpi();
    dispatch(setOverviewKpi(res.data));
    dispatch(setLoading("overview"));
  } catch (err) {
    dispatch(setLoading("overview"));
    throw new Error(err);
  }
};

export const KpiActions = {
  getPayrollKpi,
  getLeavesKpi,
  getOverviewKpi,
};

// ── Selectors ──
export const selectLeaves = (state) => state.kpi.leaves;
export const selectPayroll = (state) => state.kpi.payroll;
export const selectOverview = (state) => state.kpi.overview;
export const selectActiveTab = (state) => state.kpi.activeTab;
